import React, { useEffect, useState } from "react";

const LOGO = "/icon-192.png";

/**
 * Cross-platform PWA install button.
 *  - Chrome/Edge desktop & Android: captures `beforeinstallprompt`, fires native prompt
 *  - iOS Safari: shows a "How to install on iPhone" sheet with Share → Add to Home Screen instructions
 *  - Already-installed: hides itself
 */
export default function InstallAppButton({ variant = "header" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    // already running standalone? hide everything.
    const isStandalone =
      window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    const iosStandalone = window.navigator.standalone === true;
    if (isStandalone || iosStandalone) {
      setInstalled(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === "accepted") setInstalled(true);
      } catch {/* ignore */}
      setDeferredPrompt(null);
      return;
    }
    if (isIos) {
      setShowIosSheet(true);
      return;
    }
    // Desktop browsers that don't fire beforeinstallprompt yet (e.g. user
    // hasn't met the engagement heuristic) — show a help sheet too.
    setShowIosSheet(true);
  };

  if (installed) return null;

  // Header pill / inline variants share the same handler
  const isHeader = variant === "header";

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="install-app-btn"
        className={isHeader ? "btn btn-primary text-xs sm:text-sm px-2.5 sm:px-4 py-1.5 sm:py-2" : "btn btn-red"}
        title="Install Sean's Summer Science Lab as an app"
        aria-label="Install App"
      >
        <span className="sm:hidden" aria-hidden>⬇</span>
        <span className="hidden sm:inline">⬇ Install App</span>
      </button>

      {showIosSheet && (
        <InstallHelpSheet isIos={isIos} onClose={() => setShowIosSheet(false)} />
      )}
    </>
  );
}

function InstallHelpSheet({ isIos, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(14,18,48,0.55)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full tint-sun animate-pop-in relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="install-help-sheet"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-[var(--ink)] flex items-center justify-center text-lg"
          style={{ boxShadow: "2px 2px 0 0 var(--ink)" }}
          data-testid="install-help-close"
          aria-label="close"
        >
          ×
        </button>
        <div className="flex items-center gap-3 mb-3">
          <img
            src={LOGO}
            alt="Sean Science"
            className="w-14 h-14 rounded-2xl border-[2.5px] border-[var(--ink)]"
            style={{ boxShadow: "3px 3px 0 0 var(--ink)" }}
          />
          <div>
            <div className="eyebrow text-[var(--red)]">install</div>
            <div className="font-display text-2xl text-[var(--ink)] leading-tight">
              Add Sean's Lab to your home screen
            </div>
          </div>
        </div>

        {isIos ? (
          <ol className="space-y-2 text-[var(--ink)] text-sm leading-relaxed pl-1">
            <li>
              <strong>1.</strong> Tap the <strong>Share</strong> button{" "}
              <span className="pill" style={{ fontSize: "0.6rem" }}>⬆️</span> at the bottom of Safari.
            </li>
            <li>
              <strong>2.</strong> Scroll and tap <strong>"Add to Home Screen"</strong>{" "}
              <span className="pill" style={{ fontSize: "0.6rem" }}>➕</span>.
            </li>
            <li>
              <strong>3.</strong> Tap <strong>Add</strong> in the top right — done! Sean's icon will
              live on your home screen like a real app.
            </li>
          </ol>
        ) : (
          <ol className="space-y-2 text-[var(--ink)] text-sm leading-relaxed pl-1">
            <li>
              <strong>Chrome / Edge:</strong> tap the <strong>⋮</strong> menu →{" "}
              <strong>Install app</strong> (or the ⊕ icon in the address bar).
            </li>
            <li>
              <strong>Android:</strong> tap <strong>⋮</strong> →{" "}
              <strong>Add to Home screen</strong>.
            </li>
            <li>
              <strong>Tip:</strong> if you don't see it yet, browse the lab for ~30s and try again — the
              browser unlocks Install after a tiny bit of use. 🧪
            </li>
          </ol>
        )}

        <button
          onClick={onClose}
          className="btn btn-red w-full mt-4 text-base"
          data-testid="install-help-got-it"
        >
          🧠 Got it — keep on sciencing!
        </button>
      </div>
    </div>
  );
}
